import { addDoc, arrayRemove, arrayUnion, collection, deleteDoc, doc, getDoc, getDocs, or, Timestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import Plan, { PlanCategory } from "../models/Plan";
import { query, where } from "firebase/firestore";
import UserController from "./UserController";
import User from "../models/User";
import CategoryController from "./CategoryController";

const createPlan = async (name: string, country: string, owner: string): Promise<string> => {
    const expense_categories = await CategoryController.getCategories("expense");
    const income_categories = await CategoryController.getCategories("income");

    const plan: Partial<Plan> = {
        name,
        country,
        owner,
        members: [],
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
        income_categories: income_categories.map((c, i) => ({uid: c.uid, position: i})),
        expense_categories: expense_categories.map((c, i) => ({uid: c.uid, position: i}))
    }
    const docRef = await addDoc(collection(db, "plan"), plan);
    return docRef.id;
}

const deletePlan = async (plan_id: string) => {
    const planRef = doc(db, "plan", plan_id);
    await deleteDoc(planRef);
}

const getPlans = async (user_id: string): Promise<Plan[]> => {
    const plansRef = collection(db, "plan");
    const plansQuery = query(plansRef, or(where("owner", "==", user_id), where("members", "array-contains", user_id)));
    const plansSnapshot = await getDocs(plansQuery);
    const plans: Plan[] = [];
    plansSnapshot.forEach((doc) => {
        const plan = doc.data() as Plan;
        plan.uid = doc.id;
        plans.push(plan);
    });
    return plans;
}

const getPlanMembers = async (plan_id: string): Promise<User[]> => {
    const planRef = doc(db, "plan", plan_id);
    const planSnapshot = await getDoc(planRef);
    const members = [planSnapshot.data()?.owner, ...(planSnapshot.data()?.members || [])];
    const users = await Promise.all(members.map((member) => UserController.getUser(member))).then((users) => {
        return users;
    }).catch((error) => {
        console.error("Error getting plan members:", error);
    });
    if(!users) {
        console.error("No users found");
        return [];
    }
    return users;
}

const inviteMember = async (plan_id: string, user_id: string) => {
    const planRef = doc(db, "plan", plan_id);
    await updateDoc(planRef, {
        members: arrayUnion(user_id)
    });
};

const removeMember = async (plan_id: string, user_id: string) => {
    const planRef = doc(db, "plan", plan_id);
    await updateDoc(planRef, {
        members: arrayRemove(user_id)
    });
};

const addCategory = async (plan_id: string, category: string, type: "income" | "expense") => {
    const planRef = doc(db, "plan", plan_id);
    const planSnapshot = await getDoc(planRef);
    const plan = planSnapshot.data() as Plan;
    if(type === "income") {
        await updateDoc(planRef, {
            income_categories: arrayUnion({uid: category, position: plan.income_categories.length})
        });
    } else {
        await updateDoc(planRef, {
            expense_categories: arrayUnion({uid: category, position: plan.expense_categories.length})
        });
    }
}

const updateCategoryOrder = async (plan_id: string, categories: string[], type: "income" | "expense") => {
    const planRef = doc(db, "plan", plan_id);
    const planCategories = categories.map((category, index) => ({uid: category, position: index}))
    if(type === "income") {
        await updateDoc(planRef, {
            income_categories: planCategories
        });
    } else {
        await updateDoc(planRef, {
            expense_categories: planCategories
        });
    }
}

const removeCategory = async (plan_id: string, category: string, type: "income" | "expense") => {
    const planRef = doc(db, "plan", plan_id);
    const planSnapshot = await getDoc(planRef);
    const plan = planSnapshot.data() as Plan;
    const categories = type === "income" ? plan.income_categories : plan.expense_categories;
    const newCategories = categories.filter((cat) => cat.uid !== category).map((cat, index) => {
        return {
            ...cat,
            position: index
        }
    });
    if(type === "income") {
        await updateDoc(planRef, {
            income_categories: newCategories
        });
    } else {
        await updateDoc(planRef, {
            expense_categories: newCategories
        });
    }
}

const PlanController = {
    createPlan,
    deletePlan,
    getPlans,
    inviteMember,
    removeMember,
    getPlanMembers,
    addCategory,
    updateCategoryOrder,
    removeCategory
} 

export default PlanController;